import { makeAutoObservable } from "mobx";
import { ServiceStore } from "@intrasync/stores";
import type { RootStore } from "@intrasync/stores";


class ProjectDashboardStore {
  debugMode = false;
  enableReportComputedObservability = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  refreshJobDashboard = async (jobNumber: string | number) => {
    if (!jobNumber) throw new Error("Requires jobNumber");

    return await this.executeRefresh(jobNumber, true);
  };

  executeRefresh = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    // Execute all updates in parallel for better performance
    await Promise.all([
      this.updateJobPanelStats(jobNumber, false),
      this.updateJobHardwareStats(jobNumber, false),
      this.updateJobCastingStats(jobNumber, false),
      this.updateJobIFCStats(jobNumber, false),
      this.updateJobGeneralStats(jobNumber, false),
      this.updateJobTrailerLoadsStats(jobNumber, false),
      this.updateJobFinishingStats(jobNumber, false),
    ]);

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `All Summaries Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobFinishingStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStatsFinishingCountStore = this.rootStore
      .jobStatsFinishingCountStore as ServiceStore;

    await jobStatsFinishingCountStore.get(Number(jobNumber));

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `Finish Work Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobPanelStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStatsPanelCountStore = this.rootStore
      .jobStatsPanelCountStore as ServiceStore;

    await jobStatsPanelCountStore.get(Number(jobNumber));

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `Piece Count Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobHardwareStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStatsHardwareCountStore = this.rootStore
      .jobStatsHardwareCountStore as ServiceStore;

    await jobStatsHardwareCountStore.get(Number(jobNumber));

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `Hardware Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobCastingStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStatsCastingDatesStore = this.rootStore
      .jobStatsCastingDatesStore as ServiceStore;

    await jobStatsCastingDatesStore.get(Number(jobNumber));

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `Production Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobIFCStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStatsIfcStatusStore = this.rootStore
      .jobStatsIfcStatusStore as ServiceStore;

    await jobStatsIfcStatusStore.get(Number(jobNumber));

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `Drawing Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobGeneralStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStore = this.rootStore.jobStore as ServiceStore;

    await jobStore.find({
      query: { jobNumber: jobNumber },
    });

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `General Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  updateJobTrailerLoadsStats = async (
    jobNumber: string | number,
    notifyUser: boolean
  ): Promise<void> => {
    const jobStatsTrailerLoadsStore = this.rootStore
      .jobStatsTrailerLoadsStore as ServiceStore;

    await jobStatsTrailerLoadsStore.get(Number(jobNumber));

    if (notifyUser && this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: `Dispatch Summary Refreshed`,
        options: { variant: "success", autoHideDuration: 5000 },
      });
    }
  };

  get availableJobShortcuts() {
    if (!this.rootStore?.appLayoutStore?.toolboxRoutes) return [];
    const {
      appLayoutStore: { toolboxRoutes },
    } = this.rootStore;

    return toolboxRoutes;
  }

  get flattenedRoutesList() {
    if (!this.rootStore?.routerStore?.routesList) return [];
    const {
      routerStore: { routesList },
    } = this.rootStore;

    return routesList
      .filter(
        (route) =>
          route.name &&
          route.name !== "Job Dashboard" &&
          this.availableJobShortcuts.some(
            (shortcut) => shortcut.key === route.name
          )
      )
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }
}

export default ProjectDashboardStore;
